import React, { useState, useEffect } from 'react';
// eslint-disable-next-line
import { X, Copy, Check } from 'lucide-react';
import { useAppData } from '../context/AppDataContext';

const CopyOrderModal = ({ 
  isOpen, 
  onClose, 
  orderDetails, 
  vehicles, 
  prizes, 
  proofs,
  insuranceFile,
  insurancePath,
  prizeboardFile,
  onCopy 
}) => {
  const { clients } = useAppData();
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedSections, setSelectedSections] = useState({
    orderDetails: true,
    dropDates: true,
    vehicles: false,
    prizes: false,
    design: false,
    insurancePolicy: false,
    prizeboard: false
  });
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedClient('');
      setSelectedSections({
        orderDetails: true,
        dropDates: true,
        vehicles: false,
        prizes: false,
        design: false,
        insurancePolicy: false,
        prizeboard: false
      });
    }
  }, [isOpen]);

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedSections).every(val => val);
    const newState = allSelected ? 
      Object.keys(selectedSections).reduce((acc, key) => ({ ...acc, [key]: false }), {}) :
      Object.keys(selectedSections).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    
    setSelectedSections(newState);
  };

  const handleCopy = async () => {
    if (!selectedClient) {
      alert('Please select a client');
      return;
    }

    const hasAnySelection = Object.values(selectedSections).some(val => val);
    if (!hasAnySelection) {
      alert('Please select at least one section to copy');
      return;
    }

    setIsLoading(true);

    try {
      // Build the copied data based on selections
      const copiedData = {
        clientId: selectedClient
      };

      if (selectedSections.orderDetails) {
        const { jobNumber, status, createdAt, updatedAt, clientId, ...orderDetailsOnly } = orderDetails;
        Object.assign(copiedData, {
          ...orderDetailsOnly,
          // Clear fields that shouldn't be copied
          jobNumber: '',
          campaignName: orderDetails.campaignName || '',
          // Reset status for new order
          status: "Incomplete - Need Art"
        });
      }

      if (selectedSections.dropDates && orderDetails.drops) {
        copiedData.drops = orderDetails.drops.map(drop => ({ ...drop }));
      }

      if (selectedSections.vehicles && vehicles) {
        copiedData.vehicles = vehicles.map(vehicle => ({ ...vehicle }));
      }

      if (selectedSections.prizes && prizes) {
        copiedData.prizes = prizes.map(prize => ({ ...prize }));
      }

      if (selectedSections.design && proofs && proofs.length > 0) {
        // Copy proof references (you may need to handle file copying differently)
        copiedData.designProofs = proofs.map(proof => ({
          fileUrl: proof.fileUrl,
          status: 'pending',
          approvalStage: 'admin'
        }));
      }

      if (selectedSections.insurancePolicy && insuranceFile) {
        copiedData.insuranceFile = insuranceFile;
        copiedData.insurancePath = insurancePath;
        copiedData.insuranceLinked = true;
      }

      if (selectedSections.prizeboard && prizeboardFile) {
        copiedData.prizeboardFile = prizeboardFile;
        copiedData.prizeboardUploaded = true;
      }

      // Add original job number for auto-message
      copiedData.originalJobNumber = orderDetails.jobNumber;

      // Call the parent handler with the copied data
      onCopy(copiedData);
      onClose();
    } catch (error) {
      console.error('Error copying order:', error);
      alert('Failed to copy order');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Sort clients alphabetically for the dropdown
  const sortedClients = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const selectedCount = Object.values(selectedSections).filter(Boolean).length;
  const allSelected = Object.values(selectedSections).every(val => val);

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Copy Order
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Client Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Select Client
          </label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#374151'
            }}
          >
            <option value="">Choose a client...</option>
            {sortedClients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        {/* Section Selection */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Select Sections to Copy ({selectedCount}/7)
            </label>
            <button
              onClick={handleSelectAll}
              style={{
                background: 'none',
                border: '1px solid #d1d5db',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                color: '#374151'
              }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '12px'
          }}>
            {[
              { key: 'orderDetails', label: 'Order Details', desc: 'Campaign info, size, printer, etc.' },
              { key: 'dropDates', label: 'Drop Dates', desc: 'Start dates, end dates, DDU dates' },
              { key: 'vehicles', label: 'Vehicles', desc: 'All vehicle information' },
              { key: 'prizes', label: 'Prizes', desc: 'Prize types, odds, values' },
              { key: 'design', label: 'Design Files', desc: 'Uploaded proof files' },
              { key: 'insurancePolicy', label: 'Insurance Policy', desc: 'Insurance document' },
              { key: 'prizeboard', label: 'Prizeboard', desc: 'Prizeboard file' }
            ].map(section => (
              <div
                key={section.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '8px 0',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', minWidth: '24px' }}>
                  <input
                    type="checkbox"
                    checked={selectedSections[section.key]}
                    onChange={() => handleSectionToggle(section.key)}
                    style={{ margin: '2px 12px 0 0' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    {section.label}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {section.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: 'white',
              color: '#374151',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCopy}
            disabled={!selectedClient || selectedCount === 0 || isLoading}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: (!selectedClient || selectedCount === 0 || isLoading) ? '#9ca3af' : '#3b82f6',
              color: 'white',
              fontSize: '14px',
              cursor: (!selectedClient || selectedCount === 0 || isLoading) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Copying...
              </>
            ) : (
              <>
                <Copy size={14} />
                Copy Order
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CopyOrderModal;